import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref } from 'vue';
import type { TPercentageByType } from '@/_core/entities/report/models/types';
import type { TVulnerabilityDetails } from '@/_core/entities/vulnerability/models/types';
import { useDownloadVulnerabilityImage } from '@/_core/entities/vulnerability/api/useDownloadVulnerabilityImage';
import VulnerabilitySeverity from '@/_core/entities/vulnerability/ui/VulnerabilitySeverity/VulnerabilitySeverity.vue';
import ReportSection from '@/_core/entities/report/ui/ReportSection/ReportSection.vue';
import ReportActionButton from '@/_core/entities/report/ui/ReportActionButton/ReportActionButton.vue';
import LinkIcon from '@/assets/images/svg/link.svg?component';
import CheckIcon from '@/assets/images/svg/check.svg?component';
import FalsePositiveIcon from '@/assets/images/svg/false-positive-can.svg?component';
import DownloadPicIcon from '@/assets/images/svg/download-pic.svg?component';
import { useToast } from '@/_core/shared/ui/AppToast/useToast';
import FoldersDropdown from '@/_core/entities/vulnerability/ui/FoldersDropdown/FoldersDropdown.vue';
import CreateFolderModal from '@/_core/entities/vulnerability/ui/CreateFolderModal/CreateFolderModal.vue';
import type { TFolder } from '@/_core/entities/report/models/types';
import { useFalsePositiveFolder } from '@/_core/entities/vulnerability/utils/useFalsePositiveFolder';
import { DEFAULT_FOLDER_NAMES } from '@/_core/entities/report/models/consts';


export default /*#__PURE__*/_defineComponent({
  __name: 'VulnerabilityGeneralDetails',
  props: {
    categoryPercentageMap: null,
    vulnerability: null,
    folders: null,
    areFoldersLoading: { type: Boolean },
    hasFoldersError: { type: Boolean },
    isCreateFolderModalOpen: { type: Boolean },
    isCreateFolderLoading: { type: Boolean },
    isMovingVulnToFolder: { type: Boolean },
    isFetchingVulnerability: { type: Boolean }
  },
  emits: ["open-create-folder-modal", "close-create-folder-modal", "create-folder", "move"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'open-create-folder-modal'): void;
  (e: 'close-create-folder-modal'): void;
  (e: 'create-folder', folderName: string): void;
  (e: 'move', folder: TFolder): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  categoryPercentageMap: Record<string, TPercentageByType>;
  vulnerability: TVulnerabilityDetails;
  folders: TFolder[];
  areFoldersLoading: boolean;
  hasFoldersError: boolean;
  isCreateFolderModalOpen: boolean;
  isCreateFolderLoading: boolean;
  isMovingVulnToFolder: boolean;
  isFetchingVulnerability: boolean;
};





const instance = getCurrentInstance();
const route = computed(() => instance?.proxy.$route);

const categoryEntry = computed(() => props.categoryPercentageMap[props.vulnerability.categoryId]);

const links = computed<string[]>(() => {
  if (!categoryEntry.value?.links) return [];
  try {
    return JSON.parse(categoryEntry.value.links);
  } catch {
    return [];
  }
});

const toast = useToast();

const isCopied = ref(false);

async function copyLink() {
  await navigator.clipboard.writeText(window.location.href);
  isCopied.value = true;
  setTimeout(() => (isCopied.value = false), 2000);
}

const falsePositiveFolder = useFalsePositiveFolder(computed(() => props.folders));

const { mutateAsync: downloadImage, isPending: isDownloading } = useDownloadVulnerabilityImage();

async function handleDownloadImageClick() {
  const { uid, jobId, categoryId } = props.vulnerability;
  const params = route.value?.params;
  const NEW_TAB_TARGET = '_blank';

  try {
    if (!params) {
      return toast.error({
        message: 'Failed to download vulnerability image. Missing parameters.',
      });
    }

    if (params.jobId) {
      const data = await downloadImage({
        type: 'single-scan',
        jobId,
        vulnerabilityId: uid,
        categoryId,
      });

      window.open(data.url, NEW_TAB_TARGET);
      return;
    }

    const data = await downloadImage({
      type: 'app-version',
      integrationId: params.integrationId,
      branchName: params.branchName,
      versionId: params.versionId,
      vulnerabilityId: uid,
      categoryId,
    });

    window.open(data.url, NEW_TAB_TARGET);
  } catch (errInstance) {
    console.error(errInstance);

    const hasErrorMsg =
      errInstance &&
      typeof errInstance === 'object' &&
      errInstance !== null &&
      'message' in errInstance &&
      typeof errInstance.message === 'string';

    toast.error({
      message: hasErrorMsg ? (errInstance.message as string) : 'An unexpected error occurred',
    });
  }
}

return { __sfc: true,props, emit, instance, route, categoryEntry, links, toast, isCopied, copyLink, falsePositiveFolder, downloadImage, isDownloading, handleDownloadImageClick, VulnerabilitySeverity, ReportSection, ReportActionButton, LinkIcon, CheckIcon, FalsePositiveIcon, DownloadPicIcon, FoldersDropdown, CreateFolderModal, DEFAULT_FOLDER_NAMES }
}

})